import React, { useState, useEffect, useCallback } from "react";
import { 
    Card, 
    Descriptions, 
    Row, 
    Col, 
    Statistic, 
    Table, 
    Tag, 
    Space, 
    Typography, 
    Button,
    Tooltip,
    Modal,
    Badge,
    message,
    Spin
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
    ClockCircleOutlined,
    RocketOutlined,
    ThunderboltOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";
import "./LegDetailPage.css";

const { Text } = Typography;

const LegDetailPage = () => {
    const navigate = useNavigate();
    const { taskId } = useParams();
    
    const [loading, setLoading] = useState(false);
    const [taskInfo, setTaskInfo] = useState(null);
    const [legs, setLegs] = useState([]);
    const [groundHandlingModalVisible, setGroundHandlingModalVisible] = useState(false);
    const [currentGroundHandlingData, setCurrentGroundHandlingData] = useState(null);

    // 费用类型定义
    const COST_TYPES = [
        { key: 'bh', title: 'BH', field: 'bh' },
        { key: 'acmi', title: 'ACMI', field: 'acmi' },
        { key: 'fuel', title: 'Fuel', field: 'fuel' },
        { key: 'navigation', title: 'Navigation', field: 'navigation' },
        { key: 'groundHandling', title: 'Ground Handling', field: 'groundHandling', isComposite: true },
        { key: 'landingParking', title: 'Landing/Parking', field: 'landingParking' },
        { key: 'catering', title: 'Catering', field: 'catering' },
        { key: 'transportation', title: 'Transportation', field: 'transportation' },
        { key: 'trafficRightsPermit', title: 'Traffic Rights Permit', field: 'trafficRightsPermit' },
        { key: 'positioning', title: 'Positioning', field: 'positioning' },
        { key: 'uld', title: 'ULD', field: 'uld' },
        { key: 'sita', title: 'SITA', field: 'sita' },
        { key: 'deicing', title: 'Deicing', field: 'deicing' },
        { key: 'accommodation', title: 'Accommodation', field: 'accommodation' },
        { key: 'laundry', title: 'Laundry', field: 'laundry' },
        { key: 'disinfection', title: 'Disinfection', field: 'disinfection' },
        { key: 'flightServiceCharge', title: 'Flight Service Charge', field: 'flightServiceCharge' },
        { key: 'othersFee', title: 'Others', field: 'othersFee' },
        { key: 'commission', title: 'Commission', field: 'commission' }
    ];

    // 初始化数据（模拟）
    useEffect(() => {
        loadTaskDetail();
    }, [taskId]);

    const loadTaskDetail = useCallback(async () => {
        setLoading(true);
        try {
            // TODO: 替换为真实API调用
            // const response = await settlementService.taskDetail.getTaskDetail(taskId);
            
            // 模拟数据
            await new Promise(resolve => setTimeout(resolve, 800));
            
            setTaskInfo({
                id: taskId,
                rotationNo: "R20251021-001",
                aircraftReg: "B-1234",
                routeFullPath: "PEK->HKG->BUD->PEK",
                routeDate: "2025-10-21",
                status: "COMPLETED",
                totalCostUsd: 43750.50,
                legCount: 4
            });

            setLegs([
                {
                    legId: 1,
                    sequence: 1,
                    route: "PEK-HKG",
                    flightNumber: "HU123",
                    flightDate: "2025-10-21",
                    bh: 3.25,
                    acmi: 4000,
                    fuel: 2100,
                    navigation: 850,
                    groundHandling: 500,
                    landingParking: 1200,
                    catering: 800,
                    transportation: 300,
                    deicing: 0,
                    totalCostUsd: 10250.50,
                    remarks: "正常执行",
                    costMetadata: {
                        version: "1.0",
                        costs: {
                            fuel: {
                                usd_amount: 2100,
                                source_type: "IMPORT",
                                supplier_name: "Shell Aviation",
                                updated_at: "2025-10-21T14:30:00",
                                currency: "CNY",
                                original_amount: 15000
                            },
                            acmi: {
                                usd_amount: 4000,
                                source_type: "MANUAL",
                                supplier_name: "ACMI Leasing Co.",
                                updated_at: "2025-10-21T10:00:00"
                            },
                            navigation: {
                                usd_amount: 850,
                                source_type: "PARSED",
                                updated_at: "2025-10-21T12:00:00"
                            }
                        }
                    }
                },
                {
                    legId: 2,
                    sequence: 2,
                    route: "HKG-BUD",
                    flightNumber: "HU456",
                    flightDate: "2025-10-21",
                    bh: 4.50,
                    acmi: 5500,
                    fuel: 3200,
                    navigation: 1200,
                    groundHandling: 600,
                    landingParking: 1500,
                    catering: 1000,
                    trafficRightsPermit: 800,
                    totalCostUsd: 15800,
                    remarks: "正常",
                    costMetadata: {
                        version: "1.0",
                        costs: {
                            fuel: {
                                usd_amount: 3200,
                                source_type: "IMPORT",
                                supplier_name: "Total Aviation",
                                updated_at: "2025-10-21T16:00:00"
                            },
                            acmi: {
                                usd_amount: 5500,
                                source_type: "MANUAL",
                                updated_at: "2025-10-21T11:00:00"
                            }
                        }
                    }
                },
                {
                    legId: 3,
                    sequence: 3,
                    route: "BUD-PEK",
                    flightNumber: "HU789",
                    flightDate: "2025-10-22",
                    bh: 4.75,
                    acmi: 5920,
                    fuel: 3450,
                    navigation: 1100,
                    groundHandling: 550,
                    landingParking: 1400,
                    catering: 950,
                    totalCostUsd: 17700,
                    remarks: "",
                    costMetadata: {
                        version: "1.0",
                        costs: {}
                    }
                }
            ]);
            
        } catch (error) {
            console.error('获取任务详情失败:', error);
            message.error('获取任务详情失败');
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    // 返回上一页
    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    // 获取费用及元数据
    const getCostWithMetadata = useCallback((leg, costType) => {
        const fieldName = COST_TYPES.find(ct => ct.key === costType)?.field;
        const amount = leg[fieldName];
        
        if (amount === undefined || amount === null) {
            return null;
        }

        let metadata = null;
        if (leg.costMetadata && leg.costMetadata.costs) {
            const snakeCaseField = fieldName.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            metadata = leg.costMetadata.costs[snakeCaseField];
        }

        return {
            amount: amount,
            source: metadata?.source_type || (amount > 0 ? 'MANUAL' : null),
            supplierName: metadata?.supplier_name,
            updatedAt: metadata?.updated_at,
            currency: metadata?.currency,
            originalAmount: metadata?.original_amount
        };
    }, [COST_TYPES]);

    // 获取来源标签颜色
    const getSourceColor = useCallback((sourceType) => {
        const colorMap = {
            'PARSED': 'blue',
            'MANUAL': 'green',
            'ALLOCATED': 'gold',
            'IMPORT': 'cyan'
        };
        return colorMap[sourceType] || 'default';
    }, []);

    // 获取来源文本
    const getSourceText = useCallback((sourceType) => {
        const textMap = {
            'PARSED': '识别',
            'MANUAL': '人工',
            'ALLOCATED': '分摊',
            'IMPORT': '导入'
        };
        return textMap[sourceType] || '未知';
    }, []);

    // 生成Tooltip内容
    const getSourceTooltip = useCallback((costData) => {
        if (!costData) return '';
        
        let tooltip = `来源: ${getSourceText(costData.source)}`;
        
        if (costData.supplierName) {
            tooltip += `\n供应商: ${costData.supplierName}`;
        }
        
        if (costData.updatedAt) {
            const date = new Date(costData.updatedAt);
            tooltip += `\n更新时间: ${date.toLocaleString('zh-CN')}`;
        }
        
        if (costData.currency && costData.currency !== 'USD' && costData.originalAmount) {
            tooltip += `\n原币金额: ${costData.currency} ${formatNumber(costData.originalAmount)}`;
        }
        
        return tooltip;
    }, [getSourceText]);

    // 计算Ground Handling组合金额
    const calculateGroundHandlingTotal = useCallback((leg) => {
        const fields = ['groundHandling', 'landingParking', 'catering', 'transportation', 'deicing'];
        return fields.reduce((total, field) => total + (leg[field] || 0), 0);
    }, []);

    // 显示Ground Handling详情弹窗
    const showGroundHandlingModal = useCallback((leg) => {
        setCurrentGroundHandlingData({
            leg: leg.route,
            flightNumber: leg.flightNumber,
            costs: {
                groundHandling: leg.groundHandling || 0,
                landingParking: leg.landingParking || 0,
                catering: leg.catering || 0,
                transportation: leg.transportation || 0,
                deicing: leg.deicing || 0
            },
            total: calculateGroundHandlingTotal(leg)
        });
        setGroundHandlingModalVisible(true);
    }, [calculateGroundHandlingTotal]);

    // 格式化数字
    const formatNumber = useCallback((num) => {
        if (num === null || num === undefined || isNaN(num)) return '0.00';
        return Number(num).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }, []);

    // 计算航线指标
    const getTotalBH = useCallback(() => {
        return legs.reduce((total, leg) => total + (leg.bh || 0), 0);
    }, [legs]);

    const getTotalAcmiCost = useCallback(() => {
        return legs.reduce((total, leg) => total + (leg.acmi || 0), 0);
    }, [legs]);

    const getAcmiCostPercentage = useCallback(() => {
        const totalCost = taskInfo?.totalCostUsd || 0;
        if (totalCost === 0) return 0;
        return ((getTotalAcmiCost() / totalCost) * 100).toFixed(1);
    }, [taskInfo, getTotalAcmiCost]);

    const getTotalFuelCost = useCallback(() => {
        return legs.reduce((total, leg) => total + (leg.fuel || 0), 0);
    }, [legs]);

    const getFuelCostPercentage = useCallback(() => {
        const totalCost = taskInfo?.totalCostUsd || 0;
        if (totalCost === 0) return 0;
        return ((getTotalFuelCost() / totalCost) * 100).toFixed(1);
    }, [taskInfo, getTotalFuelCost]);

    // 渲染费用单元格
    const renderCostCell = useCallback((leg, costType) => {
        const costData = getCostWithMetadata(leg, costType);
        
        if (!costData || costData.amount === null) {
            return <Tag color="error">-</Tag>;
        }
        
        if (costData.amount === 0) {
            return <Tag color="error">$0.00</Tag>;
        }

        // Ground Handling组合费用特殊处理
        if (costType === 'groundHandling') {
            const total = calculateGroundHandlingTotal(leg);
            if (total === 0) {
                return <Tag color="error">$0.00</Tag>;
            }
            return (
                <Tooltip title="点击查看Ground Handling组合费用详情">
                    <Button 
                        type="link" 
                        onClick={() => showGroundHandlingModal(leg)}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        <Space direction="vertical" size={2} style={{ alignItems: 'flex-end' }}>
                            <Text strong style={{ color: '#1890ff' }}>
                                ${formatNumber(total)}
                            </Text>
                        </Space>
                    </Button>
                </Tooltip>
            );
        }
        
        return (
            <Space direction="vertical" size={2} style={{ alignItems: 'flex-end', width: '100%' }}>
                <Text strong style={{ color: '#1890ff' }}>
                    ${formatNumber(costData.amount)}
                </Text>
                {costData.source && (
                    <Tooltip title={getSourceTooltip(costData)}>
                        <Tag 
                            color={getSourceColor(costData.source)} 
                            style={{ fontSize: '11px', margin: 0, cursor: 'help' }}
                        >
                            {getSourceText(costData.source)}
                        </Tag>
                    </Tooltip>
                )}
            </Space>
        );
    }, [getCostWithMetadata, calculateGroundHandlingTotal, showGroundHandlingModal, formatNumber, getSourceTooltip, getSourceColor, getSourceText]);

    // 表格列定义
    const columns = [
        {
            title: '段次',
            dataIndex: 'sequence',
            key: 'sequence',
            width: 80,
            fixed: 'left',
            align: 'center',
            render: (sequence) => (
                <Badge 
                    count={sequence} 
                    style={{ backgroundColor: '#1890ff' }}
                    overflowCount={99}
                />
            )
        },
        {
            title: '航线',
            dataIndex: 'route',
            key: 'route',
            width: 120,
            fixed: 'left',
            render: (route) => <Text strong>{route}</Text>
        },
        {
            title: '航班号',
            dataIndex: 'flightNumber',
            key: 'flightNumber',
            width: 100,
            fixed: 'left',
            render: (flightNumber) => (
                <Text style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {flightNumber}
                </Text>
            )
        },
        {
            title: '飞行日期',
            dataIndex: 'flightDate',
            key: 'flightDate',
            width: 120,
            fixed: 'left'
        },
        
        // 动态生成费用类型列
        ...COST_TYPES.map(costType => ({
            title: costType.title,
            dataIndex: costType.field,
            key: costType.key,
            width: costType.title.length > 12 ? 160 : (costType.title.length > 8 ? 130 : 110),
            align: 'right',
            render: (_, record) => renderCostCell(record, costType.key)
        })),
        
        {
            title: '航段小计',
            dataIndex: 'totalCostUsd',
            key: 'totalCostUsd',
            width: 140,
            fixed: 'right',
            align: 'right',
            sorter: (a, b) => a.totalCostUsd - b.totalCostUsd,
            render: (total) => (
                <Text strong style={{ color: '#1890ff', fontSize: '15px' }}>
                    ${formatNumber(total)}
                </Text>
            )
        },
        {
            title: '备注',
            dataIndex: 'remarks',
            key: 'remarks',
            width: 150,
            fixed: 'right',
            ellipsis: {
                showTitle: false
            },
            render: (remarks) => (
                <Tooltip placement="topLeft" title={remarks}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {remarks || '-'}
                    </Text>
                </Tooltip>
            )
        }
    ];

    if (loading) {
        return (
            <div className="page-container" style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" tip="加载中..." />
            </div>
        );
    }

    if (!taskInfo) {
        return (
            <div className="page-container" style={{ textAlign: 'center', padding: '100px 0' }}>
                <Text type="secondary">未找到任务信息</Text>
            </div>
        );
    }

    return (
        <div className="page-container">
            {/* 页面头部 */}
            <div className="page-header-compact">
                <div className="page-title-compact">
                    <Button 
                        type="text" 
                        icon={<ArrowLeftOutlined />} 
                        onClick={handleBack}
                        style={{ marginRight: 16 }}
                    >
                        返回
                    </Button>
                    航段详情管理
                </div>
            </div>

            {/* 任务信息卡片 */}
            <Card className="data-card" style={{ marginBottom: 16 }}>
                <div className="table-header">
                    <h3>任务基本信息</h3>
                </div>
                <Descriptions column={4} bordered size="small">
                    <Descriptions.Item label="航线">{taskInfo.routeFullPath}</Descriptions.Item>
                    <Descriptions.Item label="状态">
                        <Tag color={taskInfo.status === 'COMPLETED' ? 'success' : 'processing'}>
                            {taskInfo.status === 'COMPLETED' ? '已完成' : '进行中'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="飞机注册号">{taskInfo.aircraftReg}</Descriptions.Item>
                    <Descriptions.Item label="轮转号">{taskInfo.rotationNo}</Descriptions.Item>
                    <Descriptions.Item label="执行日期">{taskInfo.routeDate}</Descriptions.Item>
                    <Descriptions.Item label="航段数量">{taskInfo.legCount} 段</Descriptions.Item>
                    <Descriptions.Item label="总成本" span={2}>
                        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                            ${formatNumber(taskInfo.totalCostUsd)}
                        </Text>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* 航线关键指标 */}
            <Card className="data-card" style={{ marginBottom: 16 }}>
                <div className="table-header">
                    <h3>航线关键指标</h3>
                </div>
                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title={<><ClockCircleOutlined style={{ marginRight: 8 }} />整体航线BH</>}
                            value={getTotalBH()}
                            precision={2}
                            suffix="小时"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title={<><RocketOutlined style={{ marginRight: 8 }} />ACMI费用</>}
                            value={getTotalAcmiCost()}
                            precision={2}
                            prefix="$"
                            suffix={<Tag color="purple" style={{ marginLeft: 8 }}>({getAcmiCostPercentage()}%)</Tag>}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title={<><ThunderboltOutlined style={{ marginRight: 8 }} />燃油费用</>}
                            value={getTotalFuelCost()}
                            precision={2}
                            prefix="$"
                            suffix={<Tag color="red" style={{ marginLeft: 8 }}>({getFuelCostPercentage()}%)</Tag>}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                </Row>
            </Card>

            {/* 航段费用明细表 */}
            <Card className="data-card">
                <div className="table-header">
                    <h3>航段费用明细 (共 {legs.length} 段)</h3>
                </div>
                <Table
                    columns={columns}
                    dataSource={legs}
                    rowKey="legId"
                    pagination={false}
                    scroll={{ x: 3500 }}
                    size="middle"
                    bordered={false}
                />
            </Card>

            {/* Ground Handling详情弹窗 */}
            <Modal
                title="Ground Handling 组合费用详情"
                open={groundHandlingModalVisible}
                onCancel={() => setGroundHandlingModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setGroundHandlingModalVisible(false)}>
                        关闭
                    </Button>
                ]}
                width={600}
            >
                {currentGroundHandlingData && (
                    <>
                        <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="航线">{currentGroundHandlingData.leg}</Descriptions.Item>
                            <Descriptions.Item label="航班号">{currentGroundHandlingData.flightNumber}</Descriptions.Item>
                        </Descriptions>
                        
                        <Table
                            columns={[
                                { title: '费用类型', dataIndex: 'type', key: 'type' },
                                { 
                                    title: '金额 (USD)', 
                                    dataIndex: 'amount', 
                                    key: 'amount', 
                                    align: 'right',
                                    render: (amount) => (
                                        <Text strong style={{ color: amount > 0 ? '#1890ff' : '#999' }}>
                                            ${formatNumber(amount)}
                                        </Text>
                                    )
                                }
                            ]}
                            dataSource={[
                                { type: 'Ground Handling', amount: currentGroundHandlingData.costs.groundHandling },
                                { type: 'Landing/Parking', amount: currentGroundHandlingData.costs.landingParking },
                                { type: 'Catering', amount: currentGroundHandlingData.costs.catering },
                                { type: 'Transportation', amount: currentGroundHandlingData.costs.transportation },
                                { type: 'Deicing', amount: currentGroundHandlingData.costs.deicing }
                            ]}
                            rowKey="type"
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                            <Text strong>总计</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                                ${formatNumber(currentGroundHandlingData.total)}
                                            </Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                    </>
                )}
            </Modal>
        </div>
    );
};

export default LegDetailPage;

